import type {
  RankingRuleType,
  ScoreOrderListResponse,
  TournamentResponse,
} from "../../@apis/dto/response/torunament.response";

// 대회 목록 카드에 표시할 상태 뱃지 정의
export interface TournamentListStatus {
  label: string;
  /** 뱃지 배경/글자 색 클래스 */
  badgeClassName: string;
  /** 체크 아이콘 노출 여부 */
  hasCheck: boolean;
}

// 대회 목록 항목의 상태를 계산한다.
// - myStampLabel(신청 상태)이 있으면 신청 상태를 우선 표기
// - 없으면 tournamentLabel(접수 상태) 기준으로 표기
export const resolveTournamentListStatus = (
  item: TournamentResponse,
): TournamentListStatus => {
  const brown = "bg-[#fff4d8] text-[#a2845e]";
  const green = "bg-[#f2ffec] text-[#34c759]";
  const blue = "bg-[#e5f1fd] text-[#007aff]";
  const red = "bg-[#ffecec] text-[#ff3b30]";
  const grayLight = "bg-[#f2f2f7] text-black";
  const grayDark = "bg-[#8e8e93] text-white";

  // 신청 상태(myStampLabel) 우선
  if (item.myStampLabel) {
    switch (item.myStampLabel) {
      case "접수완료":
        return { label: item.myStampLabel, badgeClassName: green, hasCheck: true };
      case "승인대기":
      case "결제대기":
        return { label: item.myStampLabel, badgeClassName: brown, hasCheck: false };
      case "참가완료":
        return { label: item.myStampLabel, badgeClassName: grayDark, hasCheck: true };
      case "미입금 마감":
        return { label: item.myStampLabel, badgeClassName: red, hasCheck: false };
      default:
        return { label: item.myStampLabel, badgeClassName: grayLight, hasCheck: false };
    }
  }

  // 미신청: 접수 상태(tournamentLabel) 기준
  switch (item.tournamentLabel) {
    case "접수중":
      return { label: item.tournamentLabel, badgeClassName: blue, hasCheck: false };
    case "접수예정":
      return {
        label: item.tournamentLabel,
        badgeClassName: grayLight,
        hasCheck: false,
      };
    case "접수종료":
    case "대회종료":
      return {
        label: item.tournamentLabel,
        badgeClassName: grayDark,
        hasCheck: false,
      };
    default:
      return {
        label: item.tournamentLabel ?? "",
        badgeClassName: grayLight,
        hasCheck: false,
      };
  }
};

export const stampStatusImage = (status?: number | null) => {
  switch (status) {
    case 1:
      return "ic-application-completed.webp";
    case 2:
      return "ic-waiting-for-approval.webp";
    case 3:
      return "ic-preliminary-pass.webp";
    case 4:
      return "ic-passed.webp";
    case 5:
      return "ic-failed.webp";
    default:
      return null;
  }
};

export const resolveStampStatusLabel = (status?: number | null): string => {
  switch (status) {
    case 1:
      return "접수 완료";
    case 2:
      return "승인 대기";
    case 3:
      return "결제 대기";
    case 4:
      return "신청 완료";
    case 5:
      return "등록 마감";
    default:
      return "";
  }
};

export const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// 로그인 성별에 맞는 일정이 없을 때. myInfo.gender 는 "0" 여성, "1" 남성.
export const unavailableScheduleMessage = (gender?: string | null) => {
  if (gender === "0") return "여성 회원은 접수할 수 있는 일정이 없습니다.";
  if (gender === "1") return "남성 회원은 접수할 수 있는 일정이 없습니다.";
  return "접수할 수 있는 일정이 없습니다.";
};

// 대회 일정 성별 조건: null이면 성별 무관
export const genderLabel = (gender: number | null): string => {
  switch (gender) {
    case 0:
      return "여성";
    case 1:
      return "남성";
    default:
      return "성별 무관";
  }
};

const RANKING_RULE_LABEL: Record<RankingRuleType, string> = {
  NEW_PEORIA: "신페리오",
  STROKE_HANDICAP: "타수 핸디",
  BACK_COUNT: "백카운트",
  TIE_PRIORITY: "선순위",
};

/** 순위 표 위 안내 문구 (줄 단위). scope 는 확정 기준 단위 */
const rankingNotice = (
  rules: RankingRuleType[] | undefined,
  isFinal: boolean | undefined,
  scope: "대회" | "라운드",
): string[] => {
  // 모르는 설정 값 제외
  const names = (rules ?? [])
    .map((rule) => RANKING_RULE_LABEL[rule])
    .filter(Boolean)
    .join(", ");
  if (!isFinal) {
    return names
      ? [
          "순위 설정 적용 전 실시간 순위입니다.",
          `${scope} 종료 후 ${names}가 적용되어 순위가 변경될 수 있습니다.`,
        ]
      : [
          "현재 순위는 실시간 순위입니다.",
          `${scope} 종료 전까지 타수에 따라 순위가 변경될 수 있습니다.`,
        ];
  }
  return [
    names
      ? `${names}가 적용된 최종 순위입니다.`
      : `${scope}가 종료되어 최종 순위가 확정되었습니다.`,
  ];
};

export const formatPeoria = (value: number | undefined) =>
  value == null ? "-" : value.toFixed(1);

/** 순위 표에 점수를 보여 주는 상태: 참가·컷오프 */
export const hasRankingScore = (status: string) =>
  status === "PARTICIPATED" || status === "CUTOFF";

/** 순위 화면의 신페리오 표시 여부와 안내 문구 */
export const rankingDisplay = (
  data:
    | Pick<
        ScoreOrderListResponse,
        "isNewPeoriaApplied" | "isRankingFinal" | "enabledRankingRules"
      >
    | undefined,
  isSingleRoundView: boolean,
) => ({
  showPeoria: data?.isNewPeoriaApplied === true,
  notice: rankingNotice(
    data?.enabledRankingRules,
    data?.isRankingFinal,
    isSingleRoundView ? "라운드" : "대회",
  ),
});
